/**
 * Faixa "Disparo automatico LIGADO" com o resumo do periodo.
 *
 * Fica ENTRE o caminho e a regua porque e a frase que liga os dois: diz que os
 * toques abaixo sao os que estao valendo e resume, numa linha so, tudo o que a
 * regua fez na janela. "Retrair os resultados" recolhe as linhas de metrica
 * dos cartoes de toque (a copy fica); "Ajustar o disparo" leva para a aba de
 * configuracoes -- nao abre outra tela.
 */

import { Badge, Button, Icon, Panel } from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import type { DispatchSummary, FunnelKind } from '../types';
import { VOCABULARIO } from '../vocabulario';

function N({ valor }: { readonly valor: number }): JSX.Element {
  return <strong className="tabular-nums font-semibold text-ink">{formatCount(valor)}</strong>;
}

export function FaixaDisparo({
  kind,
  ativo,
  resumo,
  resultadosVisiveis,
  onAlternarResultados,
  onAjustar,
}: {
  readonly kind: FunnelKind;
  readonly ativo: boolean;
  readonly resumo: DispatchSummary;
  readonly resultadosVisiveis: boolean;
  readonly onAlternarResultados: () => void;
  readonly onAjustar: () => void;
}): JSX.Element {
  const v = VOCABULARIO[kind];
  return (
    <Panel tone={ativo ? 'good' : 'warning'} className="flex flex-col gap-3 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Icon name="robo" size="sm" className="text-ink-muted" />
          <h2 className="text-base font-semibold text-ink">Disparo automático</h2>
          <Badge tone={ativo ? 'good' : 'warning'} dot uppercase>
            {ativo ? 'Ligado' : 'Desligado'}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="hidden text-xs text-ink-subtle xl:inline">{v.reguaSubtitulo}</span>
          <Button variant="ghost" size="sm" icon="chevron" onClick={onAlternarResultados} aria-expanded={resultadosVisiveis} aria-controls="regua-de-toques">
            {resultadosVisiveis ? 'Retrair os resultados' : 'Mostrar os resultados'}
          </Button>
          <Button variant="accent" size="sm" icon="config" onClick={onAjustar}>
            Ajustar o disparo
          </Button>
        </div>
      </div>

      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm leading-relaxed text-ink-muted">
        <Icon name={ativo ? 'check' : 'pausa'} size="sm" className={ativo ? 'text-status-good' : 'text-status-warning'} />
        {ativo ? (
          <span>
            O disparo está <strong className="text-ink">no ar</strong> — os toques abaixo são os que estão valendo.
          </span>
        ) : (
          <span>
            O disparo está <strong className="text-ink">pausado na prévia</strong> — nenhum toque novo sai até religar em Configurações.
          </span>
        )}
        <span>
          No período: <N valor={resumo.entered} /> entraram · <N valor={resumo.whatsappSent} /> mensagens de WhatsApp
          {resumo.calls > 0 ? (
            <>
              {' '}· <N valor={resumo.calls} /> ligações da IA
            </>
          ) : null}{' '}
          · <N valor={resumo.emailsSent} /> e-mails{resumo.emailsFallback > 0 ? <> ({formatCount(resumo.emailsFallback)} de reserva)</> : null} · <N valor={resumo.emailsOpened} /> abriram o
          e-mail · <N valor={resumo.clicked} /> clicaram no botão · <N valor={resumo.queuedNow} /> na fila hoje · <N valor={resumo.leftBecausePaid} /> saíram porque pagaram ·{' '}
          <N valor={resumo.leftBecauseReplied} /> porque responderam
          {resumo.unsubscribed > 0 ? (
            <>
              {' '}· <N valor={resumo.unsubscribed} /> pediram pra sair
            </>
          ) : null}
          .
        </span>
      </p>
    </Panel>
  );
}
